import { useState } from 'react';
import { useCallData } from './hooks/useCallData';
import { useMarketingLeads } from './hooks/useMarketingLeads';
import DateRangePicker from './components/DateRangePicker';
import type { DashboardMode } from './components/DateRangePicker';
import KpiCards from './components/KpiCards';
import Charts from './components/Charts';
import CallsTable from './components/CallsTable';
import MarketingLeadsTable from './components/MarketingLeadsTable';
import { Phone, AlertCircle } from 'lucide-react';
import './VoiceAIDashboard.css';

function VoiceAIDashboard() {
  const [mode, setMode] = useState<DashboardMode>('marketing');

  const loans = useCallData(mode === 'loans');
  const marketing = useMarketingLeads(mode === 'marketing');

  const isMarketing = mode === 'marketing';

  // The shared date bar binds to whichever view is active.
  const active = isMarketing
    ? {
        startDate: marketing.startDate,
        endDate: marketing.endDate,
        setStartDate: marketing.setStartDate,
        setEndDate: marketing.setEndDate,
        onRefresh: marketing.refetch,
        loading: marketing.loading,
      }
    : {
        startDate: loans.startDate,
        endDate: loans.endDate,
        setStartDate: loans.setStartDate,
        setEndDate: loans.setEndDate,
        onRefresh: loans.refetch,
        loading: loans.loading,
      };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="header-icon">
            <Phone size={24} />
          </div>
          <div>
            <h1 className="header-title">
              {isMarketing ? 'Marketing Leads' : 'Voice AI Call Impact'}
            </h1>
            <p className="header-subtitle">
              {isMarketing
                ? 'Work AI voice-marketing leads: listen, review, and log follow-ups'
                : 'Monitor and analyze voice AI call performance'}
            </p>
          </div>
        </div>
        {!isMarketing && loans.data && (
          <div className="header-meta">
            <span className="meta-label">Date Range:</span>
            <span className="meta-value">
              {loans.data.date_range?.start} to {loans.data.date_range?.end}
            </span>
          </div>
        )}
        {isMarketing && marketing.data && (
          <div className="header-meta">
            <span className="meta-label">Total Leads:</span>
            <span className="meta-value">{marketing.total}</span>
          </div>
        )}
      </header>

      <DateRangePicker
        startDate={active.startDate}
        endDate={active.endDate}
        setStartDate={active.setStartDate}
        setEndDate={active.setEndDate}
        onRefresh={active.onRefresh}
        loading={active.loading}
        mode={mode}
        onModeChange={setMode}
      />

      {isMarketing ? (
        <>
          {marketing.error && (
            <div className="error-banner">
              <AlertCircle size={18} />
              <span>Error: {marketing.error}</span>
            </div>
          )}

          {marketing.loading && !marketing.data && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading marketing leads...</p>
            </div>
          )}

          {marketing.data && (
            <MarketingLeadsTable
              data={marketing.data}
              loading={marketing.loading}
              total={marketing.total}
              interest={marketing.interest}
              includeActioned={marketing.includeActioned}
              stage={marketing.stage}
              limit={marketing.limit}
              offset={marketing.offset}
              startDate={marketing.startDate}
              endDate={marketing.endDate}
              setInterest={marketing.setInterest}
              setIncludeActioned={marketing.setIncludeActioned}
              setStage={marketing.setStage}
              setLimit={marketing.setLimit}
              setOffset={marketing.setOffset}
              onLeadUpdated={marketing.updateLead}
            />
          )}
        </>
      ) : (
        <>
          {loans.error && (
            <div className="error-banner">
              <AlertCircle size={18} />
              <span>Error: {loans.error}</span>
            </div>
          )}

          {loans.loading && !loans.data && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading call data...</p>
            </div>
          )}

          {loans.data && (
            <>
              <KpiCards data={loans.data} />
              <Charts data={loans.data} />
              <CallsTable data={loans.data} stage={loans.stage} onStageChange={loans.setStage} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default VoiceAIDashboard;
